import type { Queue } from './queue'

import { isTrace, logger } from '../lib'
import { Gate } from './gate'
import { ArrayQueue } from './queue'

/**
 * Contract for items that can be held in a {@link ShuntingQueue}.
 *
 * Items must declare their group membership, readiness, and expiry, and must
 * be able to abort themselves when dropped.
 */
export interface ShuntingItem {
  /**
   * Unique item's name, used in debug logging.
   */
  name: string

  /**
   * Group key for items that must be ordered relative to each other, or `null`
   * for ungrouped items. All items sharing the same group key pass through the
   * same `mid` queue.
   */
  group: string | null

  /**
   * List of gates that controls whether the item can be processed.
   * Item can open, close, or use the gate after being enqueued,
   * affecting the processing of other items with the same gate.
   */
  gates: Gate[]

  /**
   * List of gates rules on when the item leaves the queue.
   */
  quitGates?: Gate[]

  /**
   * Whether the item should coalesce existing items in the queue.
   */
  coalescing: boolean

  /**
   * Optional list of item names that should be flushed from the queue when this item is enqueued.
   * This is used to implement hard flushes of related items, e.g. when a `player_close` event is enqueued,
   * all pending `player_open` and `play_start` events should be flushed from the queue
   * to prevent them from being processed after the player is closed.
   */
  flushing?: string[]

  /**
   * Whether the item is ready to be dispatched.
   * Items that are not ready stay in the queue until they become ready.
   */
  ready: boolean

  /**
   * Whether the item has failed and should be dropped.
   */
  failed: boolean

  /**
   * Whether the item has expired and should be dropped.
   */
  expired: boolean

  /**
   * Whether the item has been cancelled with a new item.
   */
  cancelled: boolean

  /**
   * Whether the item has been flushed from the queue.
   */
  flushed: boolean

  /**
   * Called when the item is dropped due to expiry.
   */
  abort(): void

  /**
   * Called when the item is cancelled with a new item.
   * Should abort the existing item to prevent it from being processed.
   */
  cancel(): void

  /**
   * Called when the item is flushed from the queue.
   */
  flush(): void
}

/**
 * A three-stage shunting queue that orders items by group membership and
 * readiness before they are consumed by a dispatcher.
 *
 * ## Stages
 * - **`in`** — raw arrival buffer; items land here on {@link enqueue}.
 * - **`mid`** — per-group holding buffers; grouped items wait here until ready.
 * - **`out`** — dispatch-ready items; {@link dequeue} pulls from here.
 *
 * ## Ordering rules
 * - Grouped items move from `in` to their group's `mid` queue immediately.
 * - Ungrouped items only advance to `out` when all `mid` queues are empty,
 *   preserving group ordering.
 * - Items in `mid` or `out` are only forwarded once their `ready` flag is set.
 *
 * ## Processing
 * Each {@link dequeue} call fully drains `in` before promoting items through
 * `mid` and `out`. This means multiple enqueued items may become available
 * from a single dequeue cycle.
 *
 * @typeParam T - The type of items held in the queue. Must implement {@link ShuntingItem}.
 *
 * @example
 * ```ts
 * const queue = new ShuntingQueue<MyItem>()
 * queue.enqueue(item)
 * const next = queue.dequeue()
 * ```
 */
export class ShuntingQueue<T extends ShuntingItem> implements Queue<T> {
  private in: Queue<T> = new ArrayQueue()
  private mid = new Map<string, Queue<T>>()
  private out: Queue<T> = new ArrayQueue()

  // open gates' seeds set
  private gates = new Set<symbol>()

  /**
   * Initializes the shunting queue and prints its initial state.
   */
  constructor() {
    this.debug()
  }

  /**
   * Adds an item to the input stage.
   *
   * @param item - The item to enqueue.
   */
  enqueue(item: T): void {
    if (item.coalescing) {
      const last = this.peekLast(item.group)
      if (last && last.name === item.name) {
        logger.trace(`coalescing event "${item.name}" with existing event in the queue`) // prettier-ignore
        last.cancel() // cancel (=abort) existing event to prevent it from being processed

        this.in.enqueue(item)
        this.debug()
        return
      }
    }

    if (item.flushing && item.flushing.length > 0) {
      const flushingSet = new Set(item.flushing)
      for (const queue of [this.in, ...this.mid.values(), this.out]) {
        for (const item of queue) {
          if (flushingSet.has(item.name)) {
            item.flush() // forcefully flush the item from the queue
          }
        }
      }
    }

    for (const gate of item.gates) {
      const isOpen = this.gates.has(gate.seed)

      // open gate if event opens it, but abort if it try to open already open gate to prevent duplicates
      if (gate.opens) {
        if (isOpen) {
          logger.warn(`gate "${Gate.toString(gate.seed)}" already open — duplicate "${item.name}" dropped`) // prettier-ignore
          item.abort()
          return
        } else {
          this.gates.add(gate.seed)
        }
      }

      // close gate if event closes it, but abort if it tries to close already closed gate to prevent duplicates
      else if (gate.closes) {
        if (isOpen) {
          this.gates.delete(gate.seed)
        } else {
          logger.warn(`gate "${Gate.toString(gate.seed)}" already closed — duplicate "${item.name}" dropped`) // prettier-ignore
          item.abort()
          return
        }
      }

      // abort if event uses a gate that is currently closed
      else if (gate.uses) {
        if (!isOpen) {
          logger.warn(`gate "${Gate.toString(gate.seed)}" is closed — "${item.name}" dropped`) // prettier-ignore
          item.abort()
          return
        }
      }
    }

    this.in.enqueue(item)
    this.debug()
  }

  /**
   * Fully drains the input stage, advances `mid`, and returns the next
   * dispatch-ready item from `out`, or `undefined` if none is ready.
   *
   * Running the full pipeline on each call means that several items enqueued
   * since the last dequeue are all considered in a single cycle.
   *
   * @returns The next ready item, or `undefined`.
   */
  dequeue(): T | undefined {
    this.processIn()
    this.processMid()
    return this.processOut()
  }

  /**
   * Returns the next dispatch-ready item without removing it.
   *
   * @returns The front of the output stage, or `undefined` if empty.
   */
  peek(): T | undefined {
    return this.out.peek()
  }

  /**
   * Returns the last item in the queue that matches the specified group,
   * or the last item in the queue if no group is specified.
   *
   * @param group - The group to filter by.
   * @returns The last item in the specified group, or the last item (or `undefined`) overall.
   */
  peekLast(group?: string | null): T | undefined {
    // last item in `in` queue
    let item = this.in.peekLast()
    if (item) return item

    // last item in the specified `mid` queue if group is provided
    if (group) {
      const queue = this.mid.get(group)
      if (queue) {
        item = queue.peekLast()
        if (item) return item
      }
    }

    // last item in `out` queue, but only if all `mid` queues are empty to preserve group ordering
    let l = 0
    for (const queue of this.mid.values()) {
      l += queue.length
    }
    return l === 0 ? this.out.peekLast() : undefined
  }

  /**
   * Total number of items across all three stages.
   */
  get length(): number {
    let l = this.in.length + this.out.length
    for (const queue of this.mid.values()) {
      l += queue.length
    }
    return l
  }

  /**
   * Returns `true` when all three stages are empty.
   */
  isEmpty(): boolean {
    return this.length === 0
  }

  /**
   * Peeks at the front of a queue, discarding expired items along the way.
   *
   * For each expired item, {@link ShuntingItem.abort} is called.
   *
   * @param queue - The queue to peek into.
   * @returns The first non-expired item, or `undefined`.
   */
  private peekIn(queue: Queue<T>): T | undefined {
    let item: T | undefined
    while ((item = queue.peek()) && (item.expired || item.failed)) {
      queue.dequeue()

      // do not touch gates state for cancelled items,
      // as cancelled items are just replaced by the new same ones,
      // or aborted by the ones, which already changing gates state
      if (item.cancelled) {
        this.debug()
        continue
      }

      // if event was trying to open or close a gate, we need to update the gates state
      // to prevent deadlocks caused by aborted events stuck in the queue
      for (const gate of item.gates) {
        if (gate.opens) {
          this.gates.delete(gate.seed) // close gate again if it was opened by the aborted event
        } else if (gate.closes) {
          this.gates.add(gate.seed) // reopen gate if it was closed by the aborted event
        }
      }

      logger.warn(`event "${item.name}" is expired/failed — drop it`)
      this.debug()
    }

    // Check quit gates when item is ready and leaving the queue.
    if (item && item.ready && item.quitGates != null) {
      for (const gate of item.quitGates) {
        if (gate.opens) {
          this.gates.add(gate.seed)
        } else if (gate.closes) {
          this.gates.delete(gate.seed)
        }
      }
    }

    return item
  }

  /**
   * Fully drains the input stage, routing each item to `mid` (if grouped) or
   * `out` (if ungrouped and all `mid` queues are empty).
   *
   * Ungrouped items stop advancing as soon as any `mid` queue is non-empty,
   * preserving group ordering. Processing resumes from that point on the next
   * {@link dequeue} call.
   */
  private processIn(): void {
    while (true) {
      const item = this.peekIn(this.in)
      if (!item) return

      if (item.group) {
        let queue = this.mid.get(item.group)

        if (!queue) {
          queue = new ArrayQueue()
          this.mid.set(item.group, queue)
        }

        this.in.dequeue()
        queue.enqueue(item)
        this.debug()
      } else {
        for (const queue of this.mid.values()) {
          if (!queue.isEmpty()) return
        }

        this.in.dequeue()
        this.out.enqueue(item)
        this.debug()
      }
    }
  }

  /**
   * Scans all `mid` queues and promotes any ready item to `out`.
   */
  private processMid(): void {
    for (const queue of this.mid.values()) {
      const item = this.peekIn(queue)
      if (!item || !item.ready) continue

      queue.dequeue()
      this.out.enqueue(item)
      this.debug()
    }
  }

  /**
   * Returns and removes the front item from `out` if it is ready.
   *
   * @returns The next ready item, or `undefined` if the output stage is empty
   * or the front item is not yet ready.
   */
  private processOut(): T | undefined {
    const item = this.peekIn(this.out)
    if (!item || !item.ready) return

    this.out.dequeue()
    this.debug()
    return item
  }

  *[Symbol.iterator]() {
    throw new Error('ShuntingQueue does not support iteration')
    // satisfy Iterable<T> without implementing actual iteration
    // oxlint-disable-next-line no-unreachable
    yield* []
  }

  /**
   * Debug method to print the current state of the queues.
   * Prints a visual representation of the queue's current state to the console.
   * Shows all three stages with item details and open circuits.
   */
  private debug(): void {
    // check log level beforehand to avoid unnecessary processing of all queue items for formatting
    if (!isTrace()) return

    const lines: string[] = []

    lines.push('shunting queue state:')

    lines.push(
      '│ □ gates: ' +
        (this.gates.size > 0
          ? Array.from(this.gates).map(Gate.toString).join(', ')
          : '')
    )

    lines.push(`│ → in:`)
    for (const item of [...this.in].reverse()) {
      lines.push('│   · ' + this.formatItem(item))
    }

    lines.push(`│   mid:`)
    for (const [group, queue] of this.mid.entries()) {
      if (queue.isEmpty()) continue
      lines.push(`│     ${group}:`)
      for (const item of [...queue].reverse()) {
        lines.push('│     · ' + this.formatItem(item))
      }
    }

    lines.push(`│ ← out:`)
    for (const item of [...this.out].reverse()) {
      lines.push('│   · ' + this.formatItem(item))
    }

    logger.trace(lines.join('\n'))
  }

  /**
   * Formats an item for debug display.
   */
  private formatItem(item: T): string {
    const parts: string[] = []

    if (item.flushed) parts.push('→flushed')
    else if (item.ready) parts.push('✓ready')
    if (item.cancelled) parts.push('✝cancelled')
    else if (item.failed) parts.push('✗failed')
    if (item.expired) parts.push('⏱expired')
    if (item.group) parts.push(item.group)

    for (const gate of item.gates) {
      parts.push(String(gate))
    }
    if (item.quitGates) {
      for (const gate of item.quitGates) {
        parts.push('|' + String(gate))
      }
    }

    return `${item.name} ${parts.length ? `[ ${parts.join(', ')} ]` : ''}`
  }
}
